import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { CommonService } from 'src/app/shared/services/common.service';

/**
 * Base client for the four Assisted eKYC configuration entities.
 * Endpoints per contracts-1 §10.5 — `user/ekyc-management/<entity>` with
 * `/all`, `/{code}/details/view`, `/save`, `/update`.
 *
 * The reference routes create/update through the maker-checker workflow
 * (`TempRequestService.makeRequest`). Per Amendment 1 the showcase saves directly and surfaces a
 * "pending four-eyes approval" badge instead, so the demo stays inside Assisted eKYC. Reinstate
 * the workflow routing here when the real backend is wired.
 */
export abstract class ConfigurationApiService {
  protected readonly resourceUrl: string;

  protected constructor(
    protected readonly http: HttpClient,
    protected readonly commonService: CommonService,
    applicationConfigService: ApplicationConfigService,
    resource: string,
  ) {
    this.resourceUrl = applicationConfigService.getEndpointFor(resource);
  }

  query(queryModel?: unknown): Observable<any> {
    const qs = queryModel ? this.commonService.toQueryString(queryModel) : '';
    return this.http.get<any>(`${this.resourceUrl}/all?${qs}`);
  }

  find(code: string): Observable<any> {
    return this.http.get<any>(`${this.resourceUrl}/${code}/details/view`);
  }

  create(data: unknown): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/save`, data);
  }

  update(data: unknown): Observable<any> {
    return this.http.post<any>(`${this.resourceUrl}/update`, data);
  }
}

@Injectable({ providedIn: 'root' })
export class RiskGradingService extends ConfigurationApiService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig, 'user/ekyc-management/risk-grading');
  }
}

@Injectable({ providedIn: 'root' })
export class RiskGradingScoreService extends ConfigurationApiService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig, 'user/ekyc-management/risk-grading-score');
  }

  /** Feeds the Risk Grading dropdown on the score form — contracts-1 §10.2. */
  riskGradingList(): Observable<any> {
    return this.http.get<any>(
      `${this.resourceUrl.replace('risk-grading-score', 'risk-grading')}/all` +
        `?pageNumber=0&pageSize=10000&sortBy=id&sortOrder=DESC`,
    );
  }
}

@Injectable({ providedIn: 'root' })
export class TransactionProfileService extends ConfigurationApiService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig, 'user/ekyc-management/transaction-profile');
  }
}

@Injectable({ providedIn: 'root' })
export class TransactionProfileLimitService extends ConfigurationApiService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig, 'user/ekyc-management/transaction-profile-limit');
  }

  /** Feeds the Transaction Profile dropdown on the limit form — contracts-1 §10.4. */
  transactionProfileList(): Observable<any> {
    return this.http.get<any>(
      `${this.resourceUrl.replace('transaction-profile-limit', 'transaction-profile')}/all` +
        `?pageNumber=0&pageSize=10000&status=true`,
    );
  }
}
